import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import {
  Table,
  Select,
  Input,
  Tooltip
} from 'antd';
import { notificationsAtom } from '../../../atoms/Atoms';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1 } from '../../shared-components/typography/Title';
import Sparkline from '../../shared-components/charts/Sparkline';
import Delta from '../../shared-components/charts/Delta';
import HintIcon from '../../shared-components/charts/HintIcon';
import ComparisonHint from '../../shared-components/charts/ComparisonHint';
import api from '../../../api/api';
import { thousand } from '../../../functions';
import { DAY_OFFSETS, daySeries, sumSeries, num } from './movement';

function MergedSalesAndMermaDailySummary() {
  const { t } = useTranslation();
  const { Option, OptGroup } = Select;

  // Only the last seven days are shown, so only those sorts are offered. The
  // dropdown used to carry 52 options, more than half of them sorting by
  // columns that were hidden from the table.
  const daySortOptions = [
    { offset: 1, sales: 22, merma: 45, sold: t('yesterday_sales'), lost: t('yesterday_merma') },
    { offset: 2, sales: 20, merma: 43, sold: t('two_days_ago_sales'), lost: t('two_days_ago_merma') },
    { offset: 3, sales: 18, merma: 41, sold: t('three_days_ago_sales'), lost: t('three_days_ago_merma') },
    { offset: 4, sales: 16, merma: 39, sold: t('four_days_ago_sales'), lost: t('four_days_ago_merma') },
    { offset: 5, sales: 14, merma: 37, sold: t('five_days_ago_sales'), lost: t('five_days_ago_merma') },
    { offset: 6, sales: 12, merma: 35, sold: t('six_days_ago_sales'), lost: t('six_days_ago_merma') },
    { offset: 7, sales: 10, merma: 33, sold: t('seven_days_ago_sales'), lost: t('seven_days_ago_merma') },
  ];

  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [loading, setLoading] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [selectedSortFilter, setSelectedSortFilter] = useState(22);
  const [selectedCategory, setSelectedCategory] = useState(null);

  function getCategories() {
    setLoadingCategories(true);
    api.get('window/summaries/salesmerma/cateogries')
      .then((response) => {
        setLoadingCategories(false);
        if (response.data.success) {
          setCategories(response.data.data);
          if (selectedCategory == null) {
            setSelectedCategory(_.filter(response.data.data, (c) => c)[0]);
          }
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoadingCategories(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      })
  }

  function search(value, page) {
    if (selectedCategory == null) {
      return;
    }
    setLastSearchTerm(value);
    setCurrentPage(page);
    setLoading(true);
    api
      .post(
        'window/summaries/salesmerma/search',
        {
          Query: value,
          Size: pageSize,
          Page: page,
          Sort: selectedSortFilter,
          Category: selectedCategory
        }
      )
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setSearchResult(response.data.data);
        } else {
          setSearchResult({
            ...searchResult,
            items: []
          });
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function onSearch(value) {
    search(value, 0);
  }

  const onTableChange = (page, size) => {
    if (page - 1 !== currentPage) {
      setCurrentPage(page - 1);
      search(lastSearchTerm, page - 1);
    } else {
      setPageSize(size);
    }
  };

  useEffect(() => {
    search(lastSearchTerm, 0);
  }, [selectedSortFilter, selectedCategory, pageSize]);

  useEffect(() => {
    setSelectedSortFilter(22);
    getCategories();
  }, []);

  // The exact per-day figures are no longer columns of their own -- they live
  // in the sparkline's tooltip, which keeps the row scannable without losing
  // any of the numbers.
  //
  // showBalance is off by default because balance is a PER-STORE figure only:
  // MergedSalesMermaDailyWindowByStoreDto carries BalanceXDaysAgo, and the
  // product-level MergedSalesMermaDailyWindowDto does not. Showing the column
  // on the product table would render a row of zeros.
  function DayBreakdown({ row, showBalance }) {
    const sold = daySeries(row, 'sales');
    const lost = daySeries(row, 'merma');
    const received = daySeries(row, 'receiving');
    const balance = daySeries(row, 'balance');
    const cols = showBalance ? 'grid-cols-5' : 'grid-cols-4';
    return (
      <div className="text-xs" style={{ minWidth: showBalance ? '18rem' : '15rem' }}>
        <div className={`mb-1 grid ${cols} gap-x-4 whitespace-nowrap font-semibold opacity-70`}>
          <span>{t('date')}</span>
          <span className="text-right">{t('sold')}</span>
          <span className="text-right">{t('lost')}</span>
          <span className="text-right">{t('received')}</span>
          {showBalance && <span className="text-right">{t('balance')}</span>}
        </div>
        {_.map(DAY_OFFSETS, (offset, i) => (
          <div key={offset} className={`grid ${cols} gap-x-4 whitespace-nowrap tabular-nums`}>
            <span>{dayjs().subtract(offset, 'day').format('ddd DD')}</span>
            <span className="text-right">{thousand(sold[i])}</span>
            <span className="text-right">{thousand(lost[i])}</span>
            <span className="text-right">{thousand(received[i])}</span>
            {showBalance && <span className="text-right">{thousand(balance[i])}</span>}
          </div>
        ))}
      </div>
    );
  }

  function Stacked({ value, sub }) {
    return (
      <div className="tabular-nums">
        <div className="font-semibold text-ink">{value}</div>
        {sub != null && <div className="text-xs text-muted">{sub}</div>}
      </div>
    );
  }

  // The product table and the per-store table share this shape, so the columns
  // are built once instead of being copy-pasted per day and per table.
  function movementColumns(leading, { showBalance = false } = {}) {
    return [
      leading,
      {
        title: t('one_week_trend'),
        key: 'trend',
        align: 'left',
        width: 130,
        render: (row) => (
          <Tooltip styles={{ root: { maxWidth: 'none' } }} title={<DayBreakdown row={row} showBalance={showBalance} />} placement="right">
            <div className="group inline-flex cursor-help items-start gap-1">
              <Sparkline values={daySeries(row, 'sales')} />
              <HintIcon />
            </div>
          </Tooltip>
        ),
      },
      {
        title: t('sold'),
        key: 'sold',
        align: 'right',
        render: (row) => {
          const sold = daySeries(row, 'sales');
          const total = num(row.sevenDayTotalMovement) || sumSeries(sold);
          const avg = num(row.sevenDayAverageMovement) || total / sold.length;
          return <Stacked value={thousand(total)} sub={`${thousand(avg.toFixed(1))} / ${t('daily')}`} />;
        },
      },
      {
        title: t('lost'),
        key: 'lost',
        align: 'right',
        render: (row) => {
          const lost = sumSeries(daySeries(row, 'merma'));
          const sold = num(row.sevenDayTotalMovement) || sumSeries(daySeries(row, 'sales'));
          const share = sold + lost > 0 ? (lost / (sold + lost)) * 100 : 0;
          return (
            <Stacked
              value={<span className={lost > 0 ? 'text-danger' : undefined}>{thousand(lost)}</span>}
              sub={`${share.toFixed(1)}%`}
            />
          );
        },
      },
      {
        title: t('received'),
        key: 'received',
        align: 'right',
        render: (row) => {
          const received = daySeries(row, 'receiving');
          const total = num(row.sevenDayTotalReceiving) || sumSeries(received);
          const avg = num(row.sevenDayAverageReceiving) || total / received.length;
          return <Stacked value={thousand(total)} sub={`${thousand(avg.toFixed(1))} / ${t('daily')}`} />;
        },
      },
      {
        title: t('four_week_trend'),
        key: 'fourWeek',
        align: 'right',
        render: (row) => {
          const weekAvg = num(row.sevenDayAverageMovement);
          const monthAvg = num(row.fourWeekAverageMovement);
          if (!monthAvg) {
            return <span className="text-muted">—</span>;
          }
          return (
            <Tooltip
              styles={{ root: { maxWidth: 'none' } }}
              placement="left"
              title={(
                <div className="text-xs tabular-nums" style={{ minWidth: '12rem' }}>
                  <div className="flex justify-between gap-4">
                    <span>{t('total')} {t('sold')}</span>
                    <span>{thousand(row.fourWeekTotalMovement)}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span>{t('total')} {t('received')}</span>
                    <span>{thousand(row.fourWeekTotalReceiving)}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span>{t('average')} {t('sold')}</span>
                    <span>{thousand(monthAvg.toFixed(1))}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span>{t('average')} {t('received')}</span>
                    <span>{thousand(num(row.fourWeekAverageReceiving).toFixed(1))}</span>
                  </div>
                </div>
              )}
            >
              <div className="group cursor-help">
                <span className="inline-flex items-center gap-1">
                  <Delta current={weekAvg} previous={monthAvg} />
                  <HintIcon />
                </span>
                <div className="text-xs text-muted tabular-nums">
                  {thousand(monthAvg.toFixed(1))} / {t('daily')}
                </div>
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: (
          <div>
            {dayjs().subtract(1, 'day').format('dddd')}
            <div className="text-xs font-normal opacity-70">{t('one_day_ago')}</div>
          </div>
        ),
        key: 'yesterday',
        align: 'right',
        render: (row) => (
          <Tooltip
            styles={{ root: { maxWidth: 'none' } }}
            placement="left"
            title={(
              <ComparisonHint
                currentLabel={dayjs().subtract(1, 'day').format('dddd')}
                currentValue={row.salesOneDayAgo}
                previousLabel={dayjs().subtract(2, 'day').format('dddd')}
                previousValue={row.salesTwoDaysAgo}
              />
            )}
          >
            <div className="group cursor-help tabular-nums">
              <div className="font-semibold text-ink">{thousand(row.salesOneDayAgo)}</div>
              <span className="inline-flex items-center gap-1">
                <Delta current={row.salesOneDayAgo} previous={row.salesTwoDaysAgo} />
                <HintIcon />
              </span>
            </div>
          </Tooltip>
        ),
      },
    ];
  }

  return (
    <MainPage>
      <div className="flex mt-4">
        <H1 text={t('daily_movement')} />
      </div>
      <div className="lg:flex items-center justify-center">
        <Input.Search
          placeholder={`${t('search')}...`}
          allowClear
          disabled={loadingCategories}
          className="mt-4"
          enterButton={t('search')}
          size="large"
          onClear={() => onSearch('')}
          onSearch={(val) => onSearch(val)}
        />
        <div className="grid grid-cols-2 md:grid-cols-4 md:space-x-2 lg:flex">
          <Select
            loading={loadingCategories}
            className="h-10 col-span-1 mt-2 lg:mt-4 lg:w-48 mr-1 md:mr-0 ml-0 lg:ml-2"
            value={selectedCategory}
            disabled={loadingCategories}
            onChange={(value) => setSelectedCategory(value)}
          >
            {_.map(categories, (category) => (
              <Option
                key={category ?? '[no_data]'}
                value={category ?? '[no_data]'}
                className="text-sm flex items-center text-start"
              >
                {category ?? t('[no_data]')}
              </Option>
            ))}
          </Select>
          <Select
            className="h-10 col-span-1 mt-2 lg:mt-4 lg:w-56 ml-1 md:ml-0"
            value={selectedSortFilter}
            disabled={loadingCategories}
            onChange={(value) => setSelectedSortFilter(value)}
          >
            <OptGroup label={t('product')}>
              <Option value={1}>{t('name')} A-Z</Option>
              <Option value={0}>{t('name')} Z-A</Option>
              <Option value={3}>{t('category')} A-Z</Option>
              <Option value={2}>{t('category')} Z-A</Option>
            </OptGroup>
            <OptGroup label={t('sold')}>
              {_.map(daySortOptions, (d) => (
                <Option key={`sold-${d.offset}`} value={d.sales}>
                  {d.sold} {t('desc')}
                </Option>
              ))}
            </OptGroup>
            <OptGroup label={t('lost')}>
              {_.map(daySortOptions, (d) => (
                <Option key={`lost-${d.offset}`} value={d.merma}>
                  {d.lost} {t('desc')}
                </Option>
              ))}
            </OptGroup>
          </Select>
        </div>
      </div>
      <div className="mt-4">{t('found_in_search')}: {searchResult?.items ? thousand(searchResult?.total) : 0}</div>
      <Table
        locale={{
          emptyText: t('no_data')
        }}
        bordered
        size="small"
        className="mt-2"
        scroll={{ x: 'max-content' }}
        loading={loading}
        rowKey="itemCode"
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage + 1,
          total: searchResult?.total,
          onChange: onTableChange
        }}
        dataSource={searchResult?.items}
        columns={movementColumns({
          title: t('product'),
          key: 'name',
          align: 'left',
          render: (row) => (
            <div style={{ minWidth: '11rem' }}>
              <div className="text-sm font-semibold text-ink">{row.name}</div>
              <div className="text-xs text-muted tabular-nums">{row.itemCode}</div>
            </div>
          ),
        })}
        expandable={{
          expandRowByClick: false,
          rowExpandable: () => true,
          onExpand: (expanded, record) => {
            if (expanded && !record.loadedStores && !record.loadingStores) {
              const temp = { ...searchResult };
              for (let i = 0; i < temp.items.length; i++) {
                if (temp.items[i].itemCode === record.itemCode) {
                  temp.items[i].loadingStores = true;
                }
              }
              setSearchResult(temp);
              api.get(`window/summaries/salesmerma/bystore?itemCode=${record.itemCode}`)
                .then((response) => {
                  const temp2 = { ...searchResult };
                  if (response.data.success) {
                    for (let i = 0; i < temp2.items.length; i++) {
                      if (temp2.items[i].itemCode === record.itemCode) {
                        temp2.items[i].stores = response.data.data;
                      }
                    }
                  } else {
                    setNotifications([
                      ...notifications,
                      {
                        title: t('error'),
                        description: response.data.error,
                        error: true,
                      },
                    ]);
                  }
                  for (let i = 0; i < temp2.items.length; i++) {
                    if (temp2.items[i].itemCode === record.itemCode) {
                      temp2.items[i].loadingStores = false;
                    }
                  }
                  setSearchResult(temp2);
                })
                .catch((error) => {
                  const temp2 = { ...searchResult };
                  for (let i = 0; i < temp2.items.length; i++) {
                    if (temp2.items[i].itemCode === record.itemCode) {
                      temp2.items[i].loadingStores = false;
                    }
                  }
                  setSearchResult(temp2);
                  setNotifications([
                    ...notifications,
                    {
                      title: t('error'),
                      description: error.message,
                      error: true,
                    },
                  ]);
                });
            }
          },
          expandedRowRender: (record) => (
            <div className="py-2">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                {t('store')}
              </div>
              <Table
                size="small"
                bordered
                pagination={false}
                scroll={{ x: 'max-content' }}
                rowKey={(row) => `${record.itemCode}-${row.storeName}`}
                loading={record.loadingStores}
                dataSource={record?.stores}
                locale={{ emptyText: t('no_data') }}
                columns={movementColumns({
                  title: t('store'),
                  key: 'store',
                  align: 'left',
                  render: (row) => (
                    <span className="text-sm font-semibold text-ink">{row.storeName}</span>
                  ),
                }, { showBalance: true })}
              />
            </div>
          )
        }}
      />
    </MainPage>
  );
}

export default MergedSalesAndMermaDailySummary;
